import React from 'react';
import useTranslation from '@src/hooks/useTranslation';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { isPublished, isDiscontinued } from '@src/utils/course';
import { CURRENCY_SYMBOL, BUTTON_VARIANT_CONTAINED, BUTTON_VARIANT_OUTLINED } from '@src/const/common';
import Button from '@src/components/common/button';
import { getDomain } from '@src/store/selectors/ssr';
import { COURSE_TYPE_MIRROR, COURSE_TYPE_INTERNAL_COURSE } from '@src/const/course';
import { coursePriceVisible } from '@src/utils/hardcode';

const CoursePriceWrapper = styled.div`
	width: 100%;
	display: ${props => (props.bought ? 'block' : 'grid')};
	grid-template-columns: 45fr 55fr;
	grid-column-gap: 8px;
	align-items: center;

	@media ${props => props.theme.device.desktop} {
		grid-template-columns: 50fr 50fr;
	}

	@media ${props => props.theme.device.tablet} {
		width: auto;
		display: flex;
		justify-content: flex-end;
	}

	@media ${props => props.theme.device.smTablet} {
		display: flex;
		justify-content: flex-end;
	}

	@media ${props => props.theme.device.mobile} {
		display: flex;
		justify-content: flex-end;
	}
`;

const CoursePrices = styled.div`
	text-align: end;

	@media ${props => props.theme.device.tablet} {
		display: flex;
		align-items: center;
		margin-right: 40px;
	}

	@media ${props => props.theme.device.smTablet} {
		display: flex;
		align-items: center;
	}

	@media ${props => props.theme.device.mobile} {
		display: flex;
		flex-direction: column;
	}
`;

const DiscountPrice = styled.div`
	font-size: 32px;
	font-weight: 500;
	color: ${props => props.theme.colors.primary};
	${props => ({ ...props.discountStyle })};
	white-space: nowrap;

	@media ${props => props.theme.device.tablet} {
		margin-right: 12px;
	}

	@media ${props => props.theme.device.smTablet} {
		margin-right: 12px;
	}
`;

const InitPrice = styled.div`
	font-size: 14px;
	color: #a0a0a0;
	text-decoration: line-through;

	span {
		margin-right: 4px;
	}
`;

const CTABtn = styled(Button)`
	width: 100%;
	font-size: ${props => (props.isCustomizeSellButtonText ? '18px' : '22px')};
	padding: 16px;
	white-space: nowrap;

	@media ${props => props.theme.device.tablet} {
		width: 304px;
	}

	@media ${props => props.theme.device.smTablet} {
		width: 220px;
	}

	@media ${props => props.theme.device.mobile} {
		width: ${props => (props.bought ? '100%' : '200px')};
		font-size: ${props => (props.isCustomizeSellButtonText ? '18px' : '20px')};
	}

	${props => props.isNavbarStyle && `
		width: auto !important;
		padding: 8px 24px !important;
		border: 1px solid #FFF !important;
		font-size: 16px !important;
	`}
`;

const MirrorCourseWrap = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	@media ${props => props.theme.device.desktop} {
		grid-template-columns: 50fr 50fr;
	}

	@media ${props => props.theme.device.tablet} {
		width: auto;
		display: flex;
		justify-content: flex-end;
	}

	@media ${props => props.theme.device.smTablet} {
		display: flex;
		justify-content: flex-end;
	}

	@media ${props => props.theme.device.mobile} {
		display: flex;
		justify-content: flex-end;
	}
`;

const MirrorCTAButton = styled(Button)`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	font-size: ${props => (props.isCardStyle ? '16px' : '22px')};
	height: ${props => (props.isCardStyle ? '48px' : '65px')};

	@media ${props => props.theme.device.tablet} {
		width: 200px;
		height: ${props => (props.isCardStyle ? '48px' : '62px')};
	}

	@media ${props => props.theme.device.smTablet} {
		width: 210px;
		height: ${props => (props.isCardStyle ? '48px' : '62px')};
	}

	@media ${props => props.theme.device.mobile} {
		height: 54px;
		font-size: ${props => (props.isCardStyle ? '16px' : '20px')};
	}
`;

const getButtonState = ({
	t,
	domain, isFree, paid, bought, isProductInCart, status,
	handleBuyFreeCourse, handleGoToCart, handleStartCourse, handleAddToCart,
	ctaText, isCustomizeSellButtonText, customizeSellButtonText,
}: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	t: any;
	domain: string;
	isFree: boolean;
	paid: boolean;
	bought: boolean;
	isProductInCart: boolean;
	status: string;
	handleBuyFreeCourse: () => void;
	handleGoToCart: () => void;
	handleStartCourse: () => void;
	handleAddToCart: () => void;
	ctaText?: string;
	isCustomizeSellButtonText?: boolean;
	customizeSellButtonText?: string;
}) => {
	// Hardcode custom for funmaker CTA
	const isFunmaker = domain.indexOf('funmaker.teaches.cc') > -1;
	const funmakerCustomCTA = '兌換課程';
	let btnText = ctaText;
	if (isCustomizeSellButtonText) {
		btnText = customizeSellButtonText || btnText;
	}
	if (isFree && !paid) {
		return {
			btnText: btnText || t('course:go_to_class'),
			handleClickCta: handleBuyFreeCourse,
		};
	} else if (isProductInCart) {
		return {
			btnText: t('course:in_cart'),
			handleClickCta: handleGoToCart,
		};
	} else if (bought && !paid) {
		return {
			btnText: t('course:waiting_payment'),
			handleClickCta: () => null,
		};
	} else if (paid) {
		if (isDiscontinued(status) || isPublished(status)) {
			return {
				btnText: t('course:buy_and_go_to_class'),
				handleClickCta: handleStartCourse,
			};
		}
		return {
			btnText: t('course:already_buy'),
			handleClickCta: () => null,
		};
	}

	return {
		btnText: isFunmaker ? funmakerCustomCTA : btnText || t('course:add_to_cart'),
		handleClickCta: handleAddToCart,
	};
};

interface ICoursePrice {
	showInitPrice?: boolean;
	isNavbarStyle?: boolean;
	discountStyle?: {
		[x:string]: string | number;
	};
	ctaText?: string;
	isCardStyle?: boolean;
}

const CoursePrice = ({
	showInitPrice = true, discountStyle = {}, isNavbarStyle, ctaText, isCardStyle,
}: ICoursePrice): JSX.Element => {
	const { t } = useTranslation();
	const {
		currentCourse, isProductInCart, handleAddToCart, handleGoToCart, handleBuyFreeCourse, handleStartCourse,
	} = useCurrentCourse();
	const domain = useSelector(getDomain) as string;
	const {
		status, paid, bought, is_free_course, subtotal_price, init_price, type,
		hide_price, disable_sell_button, customize_sell_button_text, sell_button_text,
	} = currentCourse;
	const isOutlinedBtn =
		(bought && !paid) || (paid && !(isDiscontinued(status) || isPublished(status))) || disable_sell_button;
	const isCoursePriceVisible = coursePriceVisible(domain);
	const showPrice = !(hide_price === true);
	const {
		btnText, handleClickCta,
	} = getButtonState({
		t,
		domain,
		isFree: is_free_course,
		paid,
		bought,
		isProductInCart,
		status,
		handleBuyFreeCourse,
		handleGoToCart,
		handleStartCourse,
		handleAddToCart,
		ctaText,
		isCustomizeSellButtonText: customize_sell_button_text,
		customizeSellButtonText: sell_button_text,
	});

	let mirrorCTAText = t('course:add_mirror_course');
	if (customize_sell_button_text) {
		mirrorCTAText = sell_button_text || mirrorCTAText;
	}
	const isMirrorCourse = type === COURSE_TYPE_MIRROR;
	const isInternalCourse = type === COURSE_TYPE_INTERNAL_COURSE;
	if (isMirrorCourse || isInternalCourse) {
		// 企業購買課
		return (
			<MirrorCourseWrap>
				<MirrorCTAButton
					isDisable={disable_sell_button}
					isCardStyle={isCardStyle}
					variant={bought && !disable_sell_button ? BUTTON_VARIANT_CONTAINED : BUTTON_VARIANT_OUTLINED}
					handleClick={() => {
						if (bought) {
							handleStartCourse();
						} else {
							handleBuyFreeCourse();
						}
					}}
				>
					{bought ? t('course:buy_and_go_to_class') : mirrorCTAText}
				</MirrorCTAButton>
			</MirrorCourseWrap>
		);
	}

	if (!isCoursePriceVisible) {
		return null;
	}
	return (
		<CoursePriceWrapper bought={bought} className="course-price__course-price-wrapper">
			{!bought && (
				<CoursePrices className="course-price__price">
					{
						showPrice && (
							<>
								<DiscountPrice discountStyle={discountStyle}>
									{is_free_course ?
										t('common:free') :
										`${CURRENCY_SYMBOL} ${Number(subtotal_price).toLocaleString()}`}
								</DiscountPrice>
								{showInitPrice && !is_free_course && init_price && (
									<InitPrice>
										<span>{t('course:initial_price')}</span>
										{`${CURRENCY_SYMBOL} ${Number(init_price).toLocaleString()}`}
									</InitPrice>
								)}
							</>
						)
					}
				</CoursePrices>
			)}
			<CTABtn
				className="course-price__cta-button"
				bought={bought || showPrice}
				isNavbarStyle={isNavbarStyle}
				variant={isOutlinedBtn ? BUTTON_VARIANT_OUTLINED : BUTTON_VARIANT_CONTAINED}
				isDisable={isOutlinedBtn || disable_sell_button}
				isCustomizeSellButtonText={customize_sell_button_text}
				handleClick={handleClickCta}
			>
				{btnText}
			</CTABtn>
		</CoursePriceWrapper>
	);
};

export default CoursePrice;
